import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { ArrowRight, MapPin } from 'lucide-react';
import { worldwideTours } from '../data/tours';
import { worldwideDestinations, worldwideRegions } from '../data/mock';

export const WorldwideDestinationPage = () => {
  const { continentId, regionId, destinationId } = useParams();
  
  // Support both 3-level (/continentId/regionId/destinationId) and legacy 2-level (/regionId/destinationId) URLs
  const actualRegionId = destinationId ? regionId : continentId;
  const actualDestinationId = destinationId || regionId;
  
  const destination = worldwideDestinations.find(d => d.slug === actualDestinationId && d.region === actualRegionId);
  const region = worldwideRegions.find(r => r.id === actualRegionId);

  if (!destination || !region) {
    return (
      <div>
        <Navigation />
        <div className="container" style={{ padding: '120px 0', textAlign: 'center' }}>
          <h1 className="hero-large">Destination Not Found</h1>
          <Link to="/worldwide-journeys" style={{ color: 'var(--gold)', textDecoration: 'none' }}>
            ← Back to Worldwide Journeys
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Filter tours for this destination
  const destinationTours = worldwideTours.filter(tour => 
    tour.destinations && tour.destinations.includes(actualDestinationId)
  );
  
  // Construct back link using continent info from destination
  const backLink = destination.continent ? 
    `/worldwide-journeys/${destination.continent}/${actualRegionId}` : 
    `/worldwide-journeys/${actualRegionId}`;

  return (
    <div>
      <Navigation />
      <WhatsAppButton variant="floating" />

      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-secondary)',
        padding: '120px 0 80px',
        textAlign: 'center'
      }}>
        <div className="container">
          <div style={{ marginBottom: '24px' }}>
            <Link 
              to={backLink}
              style={{ 
                color: 'var(--text-secondary)', 
                textDecoration: 'none',
                fontSize: '14px',
                transition: 'color 0.3s ease'
              }}
              onMouseEnter={(e) => e.target.style.color = 'var(--accent-gold)'}
              onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
            >
              ← Back to {region.name}
            </Link>
          </div>
          <h1 className="hero-large" style={{ marginBottom: '16px' }}>
            {destination.name}
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            {destination.description}
          </p>
        </div>
      </section>

      {/* Tours Grid or Coming Soon */}
      <section className="section-padding" style={{ background: 'var(--bg-primary)' }}>
        <div className="container">
          {destinationTours.length > 0 ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '40px'
            }}>
              {destinationTours.map((tour) => (
                <Link
                  key={tour.id}
                  to={`/tours/${tour.id}`}
                  style={{
                    textDecoration: 'none',
                    display: 'block',
                    borderRadius: 0,
                    overflow: 'hidden',
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-subtle)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease'
                  }}
                  className="tour-card"
                >
                  <div style={{
                    width: '100%',
                    height: '280px',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={tour.image}
                      alt={tour.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      className="tour-card-image"
                    />
                  </div>
                  <div style={{ padding: '32px' }}>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      marginBottom: '16px',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontSize: '12px',
                        padding: '4px 12px',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-secondary)'
                      }}>
                        {tour.duration}
                      </span>
                    </div>
                    <h3 className="heading-2" style={{ marginBottom: '12px' }}>
                      {tour.title}
                    </h3>
                    <p className="body-regular" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
                      {tour.subtitle}
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--text-secondary)',
                      marginBottom: '20px'
                    }}>
                      <MapPin size={16} />
                      <span className="body-small">{tour.destination}</span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      color: 'var(--text-primary)',
                      fontWeight: 500
                    }}>
                      <span>View Details</span>
                      <ArrowRight size={18} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div style={{ 
              textAlign: 'center', 
              padding: '80px 20px',
              color: 'var(--text-secondary)'
            }}>
              <h3 className="heading-2" style={{ marginBottom: '16px', color: 'var(--accent-gold-light)' }}>
                Curating {destination.name} Experiences
              </h3>
              <p className="body-large" style={{ marginBottom: '32px', maxWidth: '600px', margin: '0 auto 32px' }}>
                We're designing exceptional journeys for this destination. Contact us to discuss bespoke experiences.
              </p>
              <Link
                to="/contact"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'var(--interactive-base)',
                  color: 'var(--bg-primary)',
                  textDecoration: 'none',
                  fontWeight: 500,
                  transition: 'background 0.3s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = 'var(--accent-gold)'}
                onMouseLeave={(e) => e.target.style.background = 'var(--interactive-base)'}
              >
                Plan Your Journey
                <ArrowRight size={18} />
              </Link>
            </div>
          )}
        </div>
      </section>

      <Footer />

      <style>{`
        .tour-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.2);
        }
        .tour-card:hover .tour-card-image {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};
